import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CHAPTERS, EASE } from '../data/site';
import { INSIGHT_REPORT } from '../data/insights';
import ChapterShell from '../components/ChapterShell';
import { MaskedLines, FadeUp, Eyebrow, Counter } from '../components/Reveal';

const C = CHAPTERS[3];
const GREEN = '#20431B';

const STICKIES = [
  { id: 'normal', name: 'Normal Sticky', count: 37, bg: '#F4EFD9', ink: '#1E1E1E', d: 'Simple observations. The base layer, without them, insights lose grounding and become assumptions rather than conclusions.' },
  { id: 'orange', name: 'Interesting Observations', count: 52, bg: '#FF7E00', ink: '#FAFAF7', d: 'Directional nudges pointing toward deeper insights or strategic directions that are not immediately visible.' },
  { id: 'green', name: 'Idea Sparks', count: 17, bg: '#20431B', ink: '#FAFAF7', d: 'Emerging concepts and small interventions hinted at by users, key inputs during solution development.' },
  { id: 'red', name: 'Pain Points', count: 5, bg: '#B50A00', ink: '#FAFAF7', d: 'Not just what is happening, but why. Root causes, the actual sources of friction within the system.' },
  { id: 'black', name: 'Raw Insights', count: 23, bg: '#000515', ink: '#FAFAF7', d: 'Unspoken truths. The earliest form of insights, refined over time into sharp, actionable statements.' },
];

const THEMES = [
  'Relationships that prejudice logic',
  'Humans as carriers of systems and bottlenecks',
  'Operating logic and loopholes',
  'Busy-ness functions',
  'Bespoke customer relationships',
  'Quiet patterns of a modern age retail',
  'The architecture of choice',
  'Cycles driven by credit',
];

const OPPORTUNITIES = [
  { t: 'Structuring invisible systems, without breaking them', d: 'Kirana stores already run on informal systems built on memory and relationships. The opportunity is to structure these without disrupting their natural flow.' },
  { t: 'Reducing cognitive load while retaining control', d: 'The owner carries the entire system mentally. Offload repetitive decisions and support thinking without taking away authority.' },
  { t: 'Enabling passive data capture for better decisions', d: 'Decisions lack data not because it is unavailable, but because capturing it requires effort. Record and structure data without demanding active input.' },
  { t: 'Bridging front end speed with back end visibility', d: 'Sales happen fast but management lags behind. Connect these layers so speed is maintained while visibility improves.' },
  { t: 'Enabling trust based delegation without increasing risk', d: 'Delegation is limited by trust and fear of loss. Share responsibility while keeping risk and control in balance.' },
];

const GAPS = [
  { t: 'No unified operational system, only mental integration', d: 'Selling, stock, credit and purchasing all exist, but do not connect. The owner acts as the integrator of the entire business, in real time.' },
  { t: 'Cognitive load replaces system design', d: 'Memory and coordination are handled mentally, limiting consistency, scalability and transferability.' },
  { t: 'Data is generated continuously but never captured structurally', d: 'Every transaction creates signals. The absence of effortless capture keeps decisions instinct driven.' },
  { t: 'Distributed execution with centralised control creates bottlenecks', d: 'Work is shared across people, but authority stays with the owner, slowing decisions and making the system fragile to scale.' },
  { t: 'Speed and control cannot coexist in the current system', d: 'To keep speed, structure is avoided; to gain control, operations slow down. A constant trade off with no resolution.' },
];

const FUNNEL = [
  { v: 134, l: 'raw notes captured', w: '100%' },
  { v: 39, l: 'clusters formed', w: '72%' },
  { v: 42, l: 'insights extracted', w: '46%' },
  { v: 8, l: 'themes that structure it all', w: '24%' },
];

const Synthesize = () => {
  const [activeSticky, setActiveSticky] = useState('orange');
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [openTheme, setOpenTheme] = useState(null);
  const current = STICKIES.find((s) => s.id === activeSticky);

  return (
    <ChapterShell chapter={C} heroImg="/assets/book/new-photo2.jpg" heroCaption="Every packet a data point, none of it recorded.">

      {/* STICKY SYSTEM */}
      <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="synthesize-notes">
        <Eyebrow color={GREEN}>04 · Note Taking as a System</Eyebrow>
        <MaskedLines
          lines={['Vague data leads', 'to weak insights.']}
          className="mt-10 text-4xl md:text-6xl font-light tracking-tighter leading-[1.02]"
        />
        <FadeUp delay={0.1}>
          <p className="mt-8 text-base md:text-lg text-ink/70 leading-relaxed max-w-3xl">
            What a user says is only one layer. Their actions, intent, tone and hesitation often
            reveal more than their words. So every note was classified with a colour coded system,
            defining the nature, context and intent behind each entry. Pick a colour:
          </p>
        </FadeUp>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-5">
            {STICKIES.map((s, i) => (
              <FadeUp key={s.id} delay={i * 0.05}>
                <button
                  onClick={() => setActiveSticky(s.id)}
                  data-testid={`sticky-${s.id}`}
                  className={`relative aspect-square w-full p-5 text-left shadow-md transition-transform duration-300 hover:-translate-y-1.5 hover:rotate-[-1.5deg] ${
                    activeSticky === s.id ? 'ring-4 ring-ink ring-offset-2' : ''
                  }`}
                  style={{ background: s.bg, color: s.ink, transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 1.2}deg)` }}
                >
                  <span className="text-4xl md:text-5xl font-light tracking-tighter">{s.count}</span>
                  <span className="absolute bottom-4 left-5 right-4 text-xs font-medium uppercase tracking-[0.15em] leading-tight">
                    {s.name}
                  </span>
                </button>
              </FadeUp>
            ))}
          </div>
          <div className="lg:col-span-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="border border-ink/15 p-8 md:p-10 h-full"
                data-testid="sticky-detail"
              >
                <span className="inline-block h-4 w-16" style={{ background: current.bg }} />
                <p className="mt-6 text-2xl md:text-3xl font-light tracking-tight">{current.name}</p>
                <p className="mt-4 text-base md:text-lg text-ink/70 leading-relaxed">{current.d}</p>
                <p className="mt-8 text-sm uppercase tracking-[0.25em] text-support">
                  {current.count} notes of this kind
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* FUNNEL */}
      <section className="text-paper px-5 md:px-10 lg:px-24 py-24 md:py-36" style={{ background: GREEN }} data-testid="synthesize-funnel">
        <Eyebrow color="#FFB000">Insight Formation</Eyebrow>
        <MaskedLines
          lines={['What is happening.', 'Why it matters.', 'What can be done.']}
          className="mt-10 text-4xl md:text-6xl font-light tracking-tighter leading-[1.05]"
        />
        <FadeUp delay={0.1}>
          <p className="mt-8 text-paper/70 text-base md:text-lg leading-relaxed max-w-2xl">
            An insight is not an observation, it has to be actionable. Notes were clustered by
            similarity; clusters were reframed until multiple signals pointed at the same
            underlying truth.
          </p>
        </FadeUp>
        <div className="mt-16 space-y-4">
          {FUNNEL.map((f, i) => (
            <FadeUp key={i} delay={i * 0.07}>
              <motion.div
                initial={{ scaleX: 0.4, opacity: 0 }}
                whileInView={{ scaleX: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: EASE }}
                style={{ width: f.w, transformOrigin: 'left' }}
                className="flex items-baseline justify-between border border-paper/25 bg-paper/[0.06] px-6 md:px-10 py-6 min-w-[240px]"
              >
                <span className="text-5xl md:text-7xl font-light tracking-tighter" data-testid={`funnel-${f.v}`}>
                  <Counter to={f.v} />
                </span>
                <span className="text-xs md:text-sm uppercase tracking-[0.25em] text-paper/60 text-right">{f.l}</span>
              </motion.div>
            </FadeUp>
          ))}
        </div>

        <div className="mt-20">
          <p className="text-xs uppercase tracking-[0.3em] text-paper/50 mb-8">The Eight Themes</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-paper/20 border border-paper/20">
            {THEMES.map((t, i) => (
              <FadeUp key={i} delay={i * 0.04}>
                <div className="group flex items-baseline gap-5 p-6 md:p-8 transition-colors duration-500 hover:bg-paper" style={{ background: GREEN }}>
                  <span className="text-sm font-light text-brand-amber">{i + 1}.</span>
                  <span className="text-lg md:text-xl font-medium tracking-tight group-hover:text-ink transition-colors duration-500">{t}.</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        {/* INSIGHT ARCHIVE */}
        <div className="mt-20 border border-paper/25" data-testid="insight-archive">
          <button
            onClick={() => setArchiveOpen(!archiveOpen)}
            data-testid="insight-archive-toggle"
            aria-expanded={archiveOpen}
            className="flex w-full items-center justify-between gap-6 px-6 md:px-10 py-7 md:py-9 text-left transition-colors duration-500 hover:bg-paper/[0.06]"
          >
            <div>
              <p className="text-2xl md:text-4xl font-light tracking-tighter">The Full Insight Report</p>
              <p className="mt-2 text-xs md:text-sm uppercase tracking-[0.25em] text-paper/50">
                Every insight from the field, for those who want the full depth
              </p>
            </div>
            <motion.span
              animate={{ rotate: archiveOpen ? 45 : 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="shrink-0 text-4xl md:text-5xl font-light text-brand-amber"
            >
              +
            </motion.span>
          </button>
          <AnimatePresence initial={false}>
            {archiveOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="overflow-hidden"
              >
                <div className="border-t border-paper/20 px-6 md:px-10 py-8 md:py-10 space-y-2">
                  {INSIGHT_REPORT.map((group, gi) => (
                    <div key={gi} className="border border-paper/15">
                      <button
                        onClick={() => setOpenTheme(openTheme === gi ? null : gi)}
                        data-testid={`archive-theme-${gi + 1}`}
                        aria-expanded={openTheme === gi}
                        className="flex w-full items-baseline justify-between gap-4 px-5 md:px-7 py-5 text-left transition-colors duration-300 hover:bg-paper/[0.05]"
                      >
                        <span className="text-base md:text-xl font-medium tracking-tight">
                          <span className="text-brand-amber mr-3 text-sm font-light">{gi + 1}.</span>
                          {group.theme}
                        </span>
                        <span className="shrink-0 text-[11px] uppercase tracking-[0.2em] text-paper/50">
                          {group.insights.length} insights {openTheme === gi ? '−' : '+'}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {openTheme === gi && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.5, ease: EASE }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-6 border-t border-paper/15 px-5 md:px-7 py-7">
                              {group.insights.map((ins, ii) => (
                                <div key={ii} className="grid grid-cols-1 md:grid-cols-12 gap-3" data-testid={`archive-insight-${gi + 1}-${ii + 1}`}>
                                  <p className="md:col-span-7 text-base md:text-lg font-semibold leading-relaxed text-paper/90">
                                    <span className="text-brand-amber text-xs font-light mr-2">{gi + 1}.{ii + 1}</span>
                                    {ins.t}
                                  </p>
                                  <p className="md:col-span-5 text-sm text-paper/55 leading-relaxed">{ins.d}</p>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* OPPORTUNITIES */}
      <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="synthesize-opportunities">
        <Eyebrow color={GREEN}>Opportunity Areas</Eyebrow>
        <FadeUp delay={0.08}>
          <p className="mt-8 text-lg md:text-2xl font-medium leading-relaxed max-w-3xl text-ink/85">
            Not direct solutions, focused directions that emerge from repeated patterns, where
            intervention can create real impact without forcing unnatural change.
          </p>
        </FadeUp>
        <div className="mt-14 space-y-px bg-ink/15 border border-ink/15">
          {OPPORTUNITIES.map((o, i) => (
            <FadeUp key={i} delay={i * 0.04}>
              <div className="group grid grid-cols-1 md:grid-cols-12 gap-4 bg-paper p-8 md:p-10 transition-colors duration-500 hover:bg-brand-green hover:text-paper" data-testid={`opportunity-${i + 1}`}>
                <span className="md:col-span-1 text-sm font-light tracking-widest opacity-40">0{i + 1}</span>
                <p className="md:col-span-5 text-xl md:text-2xl font-medium tracking-tight leading-snug">{o.t}</p>
                <p className="md:col-span-6 text-sm md:text-base leading-relaxed opacity-70">{o.d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* JTBD */}
      <section className="relative overflow-hidden px-5 md:px-10 lg:px-24 py-24 md:py-36" style={{ background: '#000515' }} data-testid="synthesize-jtbd">
        <div className="bandhani-dot absolute inset-0 text-paper/[0.05] pointer-events-none" />
        <Eyebrow color="#F9526B">The Core Job To Be Done</Eyebrow>
        <MaskedLines
          lines={['"Help me run my shop', 'with clarity and control', 'across sales, stock, and money,', 'without adding extra effort', 'to my daily operations."']}
          className="relative mt-10 text-3xl md:text-5xl font-light tracking-tight leading-[1.1] text-paper"
        />
        <div className="relative mt-14 flex flex-wrap gap-3">
          {['Continuous, not one time', 'Cognitive-heavy, not just operational', 'Context-driven, not standardized', 'Risk-sensitive', 'Embedded in existing workflow'].map((t, i) => (
            <FadeUp key={i} delay={i * 0.05}>
              <span className="inline-block border border-paper/30 text-paper/80 px-5 py-2.5 text-xs md:text-sm font-semibold uppercase tracking-[0.15em]">
                {t}
              </span>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* IDEATION BAND */}
      <section className="px-5 md:px-10 lg:px-24 pb-24 md:pb-36" data-testid="synthesize-ideation">
        <Eyebrow color={GREEN}>The Ideation Phase</Eyebrow>
        <FadeUp delay={0.1}>
          <figure className="mt-12">
            <div className="overflow-hidden border border-ink/15">
              <img
                src="/assets/book/field-ideation.jpg"
                alt="Ideation cards spread across the studio table"
                loading="lazy"
                data-testid="ideation-image"
                className="w-full aspect-[16/9] object-cover"
              />
            </div>
            <figcaption className="mt-4 flex flex-col md:flex-row md:items-baseline justify-between gap-2">
              <p className="text-lg font-medium tracking-tight">Ideation cards on the studio table.</p>
              <p className="text-[11px] uppercase tracking-[0.25em] text-support">Where insights begin turning into concepts, quick visualization, context, verdict</p>
            </figcaption>
          </figure>
        </FadeUp>
      </section>

      {/* SYSTEM GAPS + CHALLENGES */}
      <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="synthesize-gaps">
        <Eyebrow color={GREEN}>System Gaps</Eyebrow>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {GAPS.map((g, i) => (
            <FadeUp key={i} delay={i * 0.05} className={`border border-ink/15 p-8 ${i === 4 ? 'md:col-span-2 xl:col-span-1' : ''}`}>
              <p className="text-4xl font-light tracking-tighter" style={{ color: GREEN }}>0{i + 1}</p>
              <p className="mt-5 text-lg font-medium tracking-tight leading-snug">{g.t}</p>
              <p className="mt-3 text-sm text-ink/60 leading-relaxed">{g.d}</p>
            </FadeUp>
          ))}
          <FadeUp delay={0.25} className="flex flex-col justify-between bg-brand-green p-8 text-paper">
            <p className="text-xs uppercase tracking-[0.3em] text-paper/60">Which leads to</p>
            <p className="mt-6 text-2xl font-light tracking-tight leading-tight">
              Two design challenges. One system.
            </p>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FadeUp className="border-2 border-brand-green p-8 md:p-12" data-testid="challenge-product">
            <p className="text-xs uppercase tracking-[0.3em]" style={{ color: GREEN }}>Design Challenge · Product Centric</p>
            <p className="mt-6 text-lg md:text-xl font-medium leading-relaxed text-ink/85">
              Design a centralised operating product, the core console for structuring and
              recording daily business transactions with their origin, purpose and context,
              without interrupting the natural speed and flow of the shop. A catalyst for
              structured working, able to support plug-in modular micro-services.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="border-2 border-brand-green p-8 md:p-12" data-testid="challenge-service">
            <p className="text-xs uppercase tracking-[0.3em]" style={{ color: GREEN }}>Design Challenge · Service Centric</p>
            <p className="mt-6 text-lg md:text-xl font-medium leading-relaxed text-ink/85">
              Design a plug and use modular micro-services ecosystem that extends the central
              system, using structured business data to deliver capabilities across market
              intelligence, finance, operations and growth. Starting essential, compounding over
              time, keeping the business lean and adaptable.
            </p>
          </FadeUp>
        </div>
      </section>
    </ChapterShell>
  );
};

export default Synthesize;
